import React, { useState } from 'react';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';

interface AgentInfo {
  id: string;
  name: string;
  role: string;
  status: 'Running' | 'Idle' | 'Scheduled';
  lastRun: string;
  summary: string;
  iconBg: string;
  initials: string;
}

const AGENTS: AgentInfo[] = [
  {
    id: 'planning-agent',
    name: 'Planning Agent',
    role: 'Playbook Compiler & Strategy Orchestrator',
    status: 'Running',
    lastRun: '10 minutes ago',
    summary: 'Drafted recovery playbook for Payroll processing; compiled Plan v3.2.',
    iconBg: 'bg-blue-100 text-blue-800',
    initials: 'PL',
  },
  {
    id: 'risk-agent',
    name: 'Risk Agent',
    role: 'Dependency Scanner & Single-Point-of-Failure Detector',
    status: 'Running',
    lastRun: '42 minutes ago',
    summary: 'Detected unhedged single-vendor VoIP PBX dependency in Customer support contract.',
    iconBg: 'bg-rose-100 text-rose-800',
    initials: 'RK',
  },
  {
    id: 'exercise-agent',
    name: 'Exercise Agent',
    role: 'Scenario Simulator & Inject Generator',
    status: 'Running',
    lastRun: '1 hour ago',
    summary: 'Prepared 11 custom scenario injects for upcoming Ransomware tabletop drill.',
    iconBg: 'bg-emerald-100 text-emerald-800',
    initials: 'EX',
  },
  {
    id: 'compliance-agent',
    name: 'Compliance Agent',
    role: 'ISO 22301:2019 Clause Evaluator',
    status: 'Idle',
    lastRun: 'Yesterday at 4:15 PM',
    summary: 'Verified Clauses 4 through 8 compliance score at 88% readiness.',
    iconBg: 'bg-purple-100 text-purple-800',
    initials: 'CP',
  },
];

export const AgentsPage: React.FC = () => {
  const [runningAgentId, setRunningAgentId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleRunAgent = (agent: AgentInfo) => {
    setRunningAgentId(agent.id);
    setTimeout(() => {
      setRunningAgentId(null);
      setSuccessMessage(`${agent.name} completed run successfully.`);
      setTimeout(() => setSuccessMessage(null), 3500);
    }, 1200);
  };

  return (
    <WorkspaceLayout
      title="Autonomous Resilience Agents"
      subtitle="Background AI agents continuously analyzing risk, generating drills, and refining continuity playbooks"
    >
      <div className="space-y-6">
        {/* Status Alert */}
        {successMessage && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs font-semibold">
            {successMessage}
          </div>
        )}

        {/* Top Summary Banner */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-sm font-bold text-slate-900">3 Agents Active in Workspace</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Continuously syncing against organizational changes, tabletop outcomes, and compliance standards.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setRunningAgentId('all');
              setTimeout(() => {
                setRunningAgentId(null);
                setSuccessMessage('Full agent swarm cycle completed.');
                setTimeout(() => setSuccessMessage(null), 3500);
              }, 1800);
            }}
            disabled={Boolean(runningAgentId)}
            className="px-4 py-2 bg-[#0F172A] hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-colors shadow-sm disabled:opacity-60"
          >
            {runningAgentId === 'all' ? 'Running swarm...' : 'Run All Agents'}
          </button>
        </div>

        {/* Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {AGENTS.map((agent) => (
            <div
              key={agent.id}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${agent.iconBg}`}
                    >
                      {agent.initials}
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-slate-900">{agent.name}</h2>
                      <p className="text-[11px] text-slate-500">{agent.role}</p>
                    </div>
                  </div>

                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                      agent.status === 'Running'
                        ? 'bg-[#DCFCE7] text-[#166534]'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {agent.status}
                  </span>
                </div>

                <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-700 leading-relaxed border border-slate-100">
                  {agent.summary}
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-400 text-[11px]">Last run: {agent.lastRun}</span>
                <button
                  type="button"
                  onClick={() => handleRunAgent(agent)}
                  disabled={Boolean(runningAgentId)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
                >
                  {runningAgentId === agent.id ? 'Executing...' : 'Trigger Run'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default AgentsPage;
